// House hub page - main navigation
// The House is a spatial metaphor for navigation - ASCII art floor plan
// with doorways leading to different sections of the site.
import { initHouseHub, cleanupHouseHub } from '../scripts/house';
import { setupBreathingEffect } from '../scripts/effects';

/**
 * ASCII floor plan of the House of Chopras
 * Each corridor (doorway) corresponds to a navigation target
 */
const HOUSE_ASCII = `
         ┌─────────────────────────────────────────┐
         │            THE HOUSE OF CHOPRAS         │
         │                                         │
         │     ┌───┐         ┌───┐         ┌───┐   │
         │     │ A │         │ V │         │ P │   │
         │     │ U │         │ I │         │ R │   │
         │     │ T │         │ D │         │ E │   │
         │     │ H │         │ E │         │ S │   │
         │     │ O │         │ O │         │ S │   │
         │     │ R │         │ S │         │   │   │
         │     └─┬─┘         └─┬─┘         └─┬─┘   │
         │       │             │             │     │
         │   ════╧═════════════╧═════════════╧═══  │
         │                                         │
         │     ┌───┐                     ┌───┐     │
         │     │ B │                     │ G │     │
         │     │ U │                     │ U │     │
         │     │ Y │                     │ E │     │
         │     │   │                     │ S │     │
         │     │   │                     │ T │     │
         │     └───┘                     └───┘     │
         │                                         │
         └─────────────────────────────────────────┘
`;

/**
 * Cryptic status messages that rotate
 */
const STATUS_MESSAGES = [
  'NODE 7 ACTIVE // AWAITING INPUT',
  'SIGNAL INTEGRITY: 94.7%',
  'THERE ARE ROOMS NOT ON THIS MAP',
  'THE MANUSCRIPT IS WATCHING',
  'dim(visitor) = [0.???, ..., 0.???]',
  '> CTRL+C TO ESCAPE (THERE IS NO ESCAPE)',
];

/**
 * Render the house hub page
 */
export function renderHouse(): void {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = '';

  const container = document.createElement('div');
  container.className = 'house-container house-container--enter';

  // Build the complete house layout
  container.innerHTML = `
    <!--
      ╔══════════════════════════════════════════════════════════════════╗
      ║  VIEW SOURCE DETECTED - WELCOME, CURIOUS ONE                     ║
      ║                                                                  ║
      ║  The buttons above lead where they claim. The ASCII labels      ║
      ║  below... lead somewhere else entirely.                          ║
      ║                                                                  ║
      ║  Click the ASCII room labels to discover:                        ║
      ║    AUTHOR → /basement (where the vectors whisper)                ║
      ║    VIDEOS → /attic (where manuscripts decay)                     ║
      ║    PRESS  → /archive (where BBS ghosts linger)                   ║
      ║    BUY    → /market (where forbidden editions trade)             ║
      ║    GUEST  → /crypt (where visitors never leave)                  ║
      ║                                                                  ║
      ║  The console speaks: __chopras.hint()                            ║
      ║                                                                  ║
      ║  "The map and the territory are not the same."                   ║
      ╚══════════════════════════════════════════════════════════════════╝
    -->
    <!-- Ambient effects layer -->
    <div class="house-noise" aria-hidden="true"></div>
    <div class="house-scanlines" aria-hidden="true"></div>
    <div class="house-glow-follow" aria-hidden="true"></div>

    <!-- Main title - skip link target -->
    <h1 class="house-title house-title--glow" id="main-content" tabindex="-1">THE HOUSE OF CHOPRAS</h1>

    <!-- Navigation corridors -->
    <nav class="house-nav" aria-label="Main navigation">
      <div class="house-corridors">
        <div class="house-corridors-row house-corridors-row--top">
          <button class="house-corridor" data-corridor="author" tabindex="0" aria-label="Author: who is Carlos Chopra?">
            <span class="corridor-label">AUTHOR</span>
            <span class="corridor-hint" aria-hidden="true">who is carlos chopra?</span>
          </button>
          <button class="house-corridor" data-corridor="videos" tabindex="0" aria-label="Videos: recovered footage">
            <span class="corridor-label">VIDEOS</span>
            <span class="corridor-hint" aria-hidden="true">recovered footage</span>
          </button>
          <button class="house-corridor" data-corridor="press" tabindex="0" aria-label="Press: what they say">
            <span class="corridor-label">PRESS</span>
            <span class="corridor-hint" aria-hidden="true">what they say</span>
          </button>
        </div>
        <div class="house-corridors-row house-corridors-row--bottom">
          <button class="house-corridor" data-corridor="purchase" tabindex="0" aria-label="Buy: acquire the text">
            <span class="corridor-label">BUY</span>
            <span class="corridor-hint" aria-hidden="true">acquire the text</span>
          </button>
          <button class="house-corridor" data-corridor="guestbook" tabindex="0" aria-label="Guestbook: leave your mark">
            <span class="corridor-label">GUEST</span>
            <span class="corridor-hint" aria-hidden="true">leave your mark</span>
          </button>
        </div>
      </div>
    </nav>

    <!-- ASCII floor plan (decorative, with interactive overlay) -->
    <div class="house-floor-plan-wrapper">
      <pre class="house-floor-plan house-floor-plan--dim" aria-hidden="true">${HOUSE_ASCII}</pre>

      <!-- Hidden trigger areas - overlay ASCII room labels -->
      <!--
        SECRET: The ASCII floor plan hides alternate destinations.
        Top buttons = real sections. ASCII labels = hidden rooms.

        AUTHOR label → /basement (AI narrator fragments, vector notation)
        VIDEOS label → /attic (Lost manuscript pages, water damage)
        PRESS label  → /archive (Sealed BBS threads from impossible dates)
        BUY label    → /market (Black market manuscript fragments)
        GUEST label  → /crypt (Visitors who checked in but never left)

        Console: __chopras.discover('basement') | 'attic' | 'archive' | 'market' | 'crypt'
      -->

      <!-- Basement: Overlays AUTHOR in ASCII -->
      <button class="house-hidden-trigger house-hidden-trigger--basement"
              data-hidden="basement"
              aria-hidden="true"
              tabindex="-1"
              title=""></button>

      <!-- Attic: Overlays VIDEOS in ASCII -->
      <button class="house-hidden-trigger house-hidden-trigger--attic"
              data-hidden="attic"
              aria-hidden="true"
              tabindex="-1"
              title=""></button>

      <!-- Archive: Overlays PRESS in ASCII -->
      <button class="house-hidden-trigger house-hidden-trigger--archive"
              data-hidden="archive"
              aria-hidden="true"
              tabindex="-1"
              title=""></button>

      <!-- Market: Overlays BUY in ASCII -->
      <button class="house-hidden-trigger house-hidden-trigger--market"
              data-hidden="market"
              aria-hidden="true"
              tabindex="-1"
              title=""></button>

      <!-- Crypt: Overlays GUEST in ASCII -->
      <button class="house-hidden-trigger house-hidden-trigger--crypt"
              data-hidden="crypt"
              aria-hidden="true"
              tabindex="-1"
              title=""></button>
    </div>

    <!-- Status line with cryptic message -->
    <div class="house-status house-status--blink" aria-live="polite">
      ${STATUS_MESSAGES[0]}
    </div>

    <!-- Return to entry link (for accessibility) -->
    <a href="#" class="house-return" aria-label="Return to the entry introduction sequence">RETURN</a>

    <!-- Footer -->
    <footer class="house-footer" aria-label="Site footer">
      <a href="https://digitalstructures.info" target="_blank" rel="noopener noreferrer" class="house-footer-logo">
        <img src="/assets/digitalstructureslogo.svg" alt="Digital Structures" class="house-footer-logo-img" />
      </a>
    </footer>

    <!-- You weren't supposed to find this -->
    <div class="house-specter" aria-hidden="true">
      <img src="/assets/chopras2.gif" alt="" class="house-specter-img" />
    </div>
  `;

  app.appendChild(container);

  // Initialize house hub interactions
  initHouseHub(container);

  // Start status message rotation
  startStatusRotation(container);

  // Setup mouse glow follow effect
  setupGlowFollow(container);

  // Focus management: move focus to the first corridor for keyboard navigation
  // This is especially important when coming from the entry skip button
  requestAnimationFrame(() => {
    const firstCorridor = container.querySelector('.house-corridor') as HTMLElement;
    if (firstCorridor) {
      firstCorridor.focus();
    }
  });

  // Remove enter animation class after it completes
  setTimeout(() => {
    container.classList.remove('house-container--enter');
  }, 500);
}

/**
 * Rotate status messages periodically
 */
let statusInterval: number | null = null;

/**
 * Cleanup function for breathing effect
 */
let breathingCleanup: (() => void) | null = null;

function startStatusRotation(container: HTMLElement): void {
  const statusEl = container.querySelector('.house-status');
  if (!statusEl) return;

  let currentIndex = 0;

  statusInterval = window.setInterval(() => {
    currentIndex = (currentIndex + 1) % STATUS_MESSAGES.length;
    statusEl.textContent = STATUS_MESSAGES[currentIndex];
  }, 8000);
}

/**
 * Setup mouse-following glow effect and breathing distortion
 */
function setupGlowFollow(container: HTMLElement): void {
  const glowEl = container.querySelector('.house-glow-follow') as HTMLElement;

  // Reduced motion check disabled to ensure full experience

  // Setup glow follow effect
  if (glowEl) {
    container.addEventListener('mousemove', (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      glowEl.style.left = `${x}px`;
      glowEl.style.top = `${y}px`;
    });
  }

  // Setup breathing distortion effect
  breathingCleanup = setupBreathingEffect(container, {
    maxSkew: 0.4,
    maxScale: 0.002,
    smoothing: 0.06,
    distortTargets: '.house-corridor, .house-floor-plan, .house-title',
    maxPerspectiveShift: 4,
  });
}

/**
 * Cleanup house page
 */
export function cleanupHouse(): void {
  // Clear status rotation interval
  if (statusInterval) {
    clearInterval(statusInterval);
    statusInterval = null;
  }

  // Cleanup breathing effect
  if (breathingCleanup) {
    breathingCleanup();
    breathingCleanup = null;
  }

  // Cleanup house hub interactions
  cleanupHouseHub();
}
